import { clamp, moveTowards, v3, type AABB, type Vec3 } from './math.js';
import {
  Button,
  DEFAULT_MOVEMENT,
  hasButton,
  type InputCommand,
  type MovementConfig,
  type MovementState,
  type WorldStatics,
} from './types.js';

/**
 * Advance one player's movement by exactly one fixed timestep.
 *
 * PURE: same (state, input, dt, config, world) always yields the same result.
 * No `Date.now()`, no `Math.random()`, no allocation of shared mutable state.
 * This is the single source of truth for movement — the client runs it for
 * prediction and the server will run it for authority. Do not fork it.
 *
 * @param state Current movement state (not mutated).
 * @param input The input command to apply this tick.
 * @param dt    Fixed timestep in seconds (e.g. 1/30).
 * @returns A new {@link MovementState}.
 */
export function stepMovement(
  state: MovementState,
  input: InputCommand,
  dt: number,
  world: WorldStatics,
  config: MovementConfig = DEFAULT_MOVEMENT,
): MovementState {
  const pos: Vec3 = { x: state.pos.x, y: state.pos.y, z: state.pos.z };
  const vel: Vec3 = { x: state.vel.x, y: state.vel.y, z: state.vel.z };

  const crouching = hasButton(input.buttons, Button.Crouch);
  const wantJump = hasButton(input.buttons, Button.Jump);
  const aiming = hasButton(input.buttons, Button.Ads);
  const sprinting =
    hasButton(input.buttons, Button.Sprint) &&
    !crouching &&
    !aiming && // can't sprint while aiming down sights
    hasButton(input.buttons, Button.Forward);

  // --- Desired horizontal direction, relative to view yaw ----------------
  let ix = 0;
  let iz = 0;
  if (hasButton(input.buttons, Button.Forward)) iz += 1;
  if (hasButton(input.buttons, Button.Back)) iz -= 1;
  if (hasButton(input.buttons, Button.Right)) ix += 1;
  if (hasButton(input.buttons, Button.Left)) ix -= 1;

  const sin = Math.sin(input.yaw);
  const cos = Math.cos(input.yaw);
  // Forward is +z in local space; rotate input into world by yaw.
  let wishX = ix * cos + iz * sin;
  let wishZ = iz * cos - ix * sin;
  const wishLen = Math.hypot(wishX, wishZ);
  if (wishLen > 1e-6) {
    wishX /= wishLen;
    wishZ /= wishLen;
  }

  const speedMul =
    (sprinting ? config.sprintMultiplier : 1) *
    (crouching ? config.crouchMultiplier : 1) *
    (aiming ? 0.6 : 1); // ADS slows movement (risk/reward)
  const targetSpeed = config.walkSpeed * speedMul;

  const targetVX = wishX * targetSpeed;
  const targetVZ = wishZ * targetSpeed;

  const onGround = state.onGround;
  if (wishLen > 1e-6) {
    const a = (onGround ? config.accel : config.accel * config.airControl) * dt;
    vel.x = moveTowards(vel.x, targetVX, a);
    vel.z = moveTowards(vel.z, targetVZ, a);
  } else if (onGround) {
    const f = config.friction * dt;
    vel.x = moveTowards(vel.x, 0, f);
    vel.z = moveTowards(vel.z, 0, f);
  }

  // --- Vertical: jump + gravity ------------------------------------------
  if (wantJump && onGround) {
    vel.y = config.jumpSpeed;
  }
  vel.y -= config.gravity * dt;

  const height = crouching ? config.crouchHeight : config.standHeight;

  // --- Integrate + resolve, one axis at a time (robust against tunnelling
  //     at these speeds/step sizes). On a horizontal block, try stepping up
  //     small ledges (stairs) when grounded. ------------------------------
  pos.x += vel.x * dt;
  resolveHorizontalWithStep(pos, vel, world.colliders, height, config, 'x', onGround);

  pos.z += vel.z * dt;
  resolveHorizontalWithStep(pos, vel, world.colliders, height, config, 'z', onGround);

  // Safety net: if we're STILL inside something (a carried-in / teleported
  // overlap with no velocity to push back against), eject along the axis of
  // least penetration. This is the only place minimum-translation is used —
  // and because it picks the THINNEST escape, it never flings the player down
  // the long axis of a wall the way per-axis min-translation did.
  unstickHorizontal(pos, world.colliders, height, config.radius);

  const prevFootY = pos.y; // feet height before the vertical move (landing rule)
  pos.y += vel.y * dt;
  let grounded = resolveVertical(
    pos,
    vel,
    world.colliders,
    height,
    config.radius,
    world.groundY,
    prevFootY,
  );

  // Ground snapping (smooth stair DESCENT): if we were walking on the ground
  // and the floor dropped by no more than a stair step, stick to it instead of
  // going briefly airborne on every step. Real ledges (> stepHeight) still
  // become a genuine fall, and jumps are never snapped.
  if (!grounded && onGround && !wantJump && vel.y <= 0) {
    const snapTo = findGroundWithin(pos, world, height, config.radius, config.stepHeight);
    if (snapTo !== null) {
      pos.y = snapTo;
      vel.y = 0;
      grounded = true;
    }
  }

  // Soft play-area clamp.
  pos.x = clamp(pos.x, world.bounds.minX, world.bounds.maxX);
  pos.z = clamp(pos.z, world.bounds.minZ, world.bounds.maxZ);

  return {
    pos,
    vel,
    yaw: input.yaw,
    pitch: input.pitch,
    onGround: grounded,
    crouching,
  };
}

/**
 * Highest walkable surface within `maxDrop` below the feet (or null). Used by
 * the descent ground-snap.
 */
function findGroundWithin(
  pos: Vec3,
  world: WorldStatics,
  height: number,
  radius: number,
  maxDrop: number,
): number | null {
  let best: number | null = null;
  for (const c of world.colliders) {
    if (
      pos.x + radius > c.min.x &&
      pos.x - radius < c.max.x &&
      pos.z + radius > c.min.z &&
      pos.z - radius < c.max.z &&
      c.max.y <= pos.y + 1e-6 &&
      c.max.y >= pos.y - maxDrop
    ) {
      if (best === null || c.max.y > best) best = c.max.y;
    }
  }
  if (world.groundY <= pos.y + 1e-6 && world.groundY >= pos.y - maxDrop) {
    if (best === null || world.groundY > best) best = world.groundY;
  }
  // Verify the snapped position is not embedded in anything.
  if (best !== null) {
    const snapped: Vec3 = { x: pos.x, y: best, z: pos.z };
    for (const c of world.colliders) {
      if (
        snapped.x + radius > c.min.x &&
        snapped.x - radius < c.max.x &&
        snapped.z + radius > c.min.z &&
        snapped.z - radius < c.max.z &&
        snapped.y + height > c.min.y &&
        snapped.y < c.max.y
      ) {
        return null;
      }
    }
  }
  return best;
}

/** True if the player AABB at `pos` overlaps `c` on all three axes. */
function overlaps(
  pos: Vec3,
  height: number,
  radius: number,
  c: AABB,
): boolean {
  return (
    pos.x + radius > c.min.x &&
    pos.x - radius < c.max.x &&
    pos.z + radius > c.min.z &&
    pos.z - radius < c.max.z &&
    pos.y + height > c.min.y &&
    pos.y < c.max.y
  );
}

/**
 * Horizontal resolution with stair support: when a grounded player runs into a
 * ledge no taller than `stepHeight` and there is headroom on top, lift them
 * onto it instead of blocking — this is what makes staircases walkable.
 */
function resolveHorizontalWithStep(
  pos: Vec3,
  vel: Vec3,
  colliders: AABB[],
  height: number,
  config: MovementConfig,
  axis: 'x' | 'z',
  grounded: boolean,
): void {
  if (grounded) {
    for (const c of colliders) {
      if (!overlaps(pos, height, config.radius, c)) continue;
      const ledge = c.max.y - pos.y;
      if (ledge > 0 && ledge <= config.stepHeight) {
        // Try the stepped-up position; accept only if it's fully clear.
        const stepped: Vec3 = { x: pos.x, y: c.max.y, z: pos.z };
        let clear = true;
        for (const o of colliders) {
          if (overlaps(stepped, height, config.radius, o)) {
            clear = false;
            break;
          }
        }
        if (clear) {
          pos.y = c.max.y;
        }
      }
    }
  }
  resolveHorizontal(pos, vel, colliders, height, config.radius, axis);
}

function resolveHorizontal(
  pos: Vec3,
  vel: Vec3,
  colliders: AABB[],
  height: number,
  radius: number,
  axis: 'x' | 'z',
): void {
  // We just integrated this axis (`pos.axis += vel.axis*dt`), so the sign of
  // the axis velocity IS the direction we entered from. Push back the way we
  // came: that face is reachable by definition and can never pop us through to
  // the FAR side. Minimum-translation on a single axis was the old bug — on a
  // wall thinner than 2*radius (the 0.8u subway track dividers), once past the
  // centerline its "nearest face" is the wrong one and the player tunnels; and
  // on a wall's broad face it would shove a straight-pressing player sideways
  // down the long axis. If this axis isn't moving, leave the overlap for the
  // axis that IS moving, or for the final unstick pass.
  const MOVING = 1e-4;
  for (const c of colliders) {
    if (!overlaps(pos, height, radius, c)) continue;
    if (axis === 'x') {
      if (vel.x > MOVING) {
        pos.x = c.min.x - radius - SKIN; // entered moving +x → push to the -x face
        vel.x = 0;
      } else if (vel.x < -MOVING) {
        pos.x = c.max.x + radius + SKIN;
        vel.x = 0;
      }
    } else {
      if (vel.z > MOVING) {
        pos.z = c.min.z - radius - SKIN;
        vel.z = 0;
      } else if (vel.z < -MOVING) {
        pos.z = c.max.z + radius + SKIN;
        vel.z = 0;
      }
    }
  }
}

// Seat the player a hair OUTSIDE each face instead of exactly on it: at a face
// like x=1, `(1 + radius) - radius` is not 1 in floating point, so an exact
// seat reads back as ~1e-16 of penetration and re-triggers resolution forever.
// 1mm is imperceptible and kills the jitter.
const SKIN = 1e-3;

/**
 * Last-resort separation for a player left embedded in geometry with no
 * velocity to resolve against (teleport, spawn, a reconciliation snap). Ejects
 * along the axis of least penetration — the genuine minimum-translation vector
 * — so the escape is always the shortest one (out the thin face of a wall, not
 * along its length). A few passes converge when several boxes overlap at once.
 */
function unstickHorizontal(
  pos: Vec3,
  colliders: AABB[],
  height: number,
  radius: number,
): void {
  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (const c of colliders) {
      if (!overlaps(pos, height, radius, c)) continue;
      const penXNeg = pos.x + radius - c.min.x; // push toward -x
      const penXPos = c.max.x - (pos.x - radius); // push toward +x
      const penZNeg = pos.z + radius - c.min.z;
      const penZPos = c.max.z - (pos.z - radius);
      const m = Math.min(penXNeg, penXPos, penZNeg, penZPos);
      if (m === penXNeg) pos.x = c.min.x - radius - SKIN;
      else if (m === penXPos) pos.x = c.max.x + radius + SKIN;
      else if (m === penZNeg) pos.z = c.min.z - radius - SKIN;
      else pos.z = c.max.z + radius + SKIN;
      moved = true;
    }
    if (!moved) return;
  }
}

/** Returns whether the player ended this tick grounded. */
function resolveVertical(
  pos: Vec3,
  vel: Vec3,
  colliders: AABB[],
  height: number,
  radius: number,
  groundY: number,
  prevFootY: number,
): boolean {
  let grounded = false;

  for (const c of colliders) {
    if (!overlaps(pos, height, radius, c)) continue;
    if (vel.y <= 0) {
      // LANDING RULE: you can only land on a top your feet were at/above
      // before this tick. Without this, brushing the SIDE of a box while
      // falling hoisted the player onto its top, and at ledge/wall corners
      // that caused a violent up-onto/pushed-off oscillation (the "stuck
      // bouncing in a wall" bug). Side contacts are left for the horizontal
      // resolver next tick.
      if (c.max.y <= prevFootY + 1e-4) {
        pos.y = c.max.y;
        grounded = true;
        vel.y = 0;
      }
    } else {
      // Rising into the bottom face.
      pos.y = c.min.y - height;
      vel.y = 0;
    }
  }

  if (pos.y <= groundY) {
    pos.y = groundY;
    if (vel.y < 0) vel.y = 0;
    grounded = true;
  }

  return grounded;
}

/** Camera eye height above feet for the given crouch state. */
export function eyeHeight(
  crouching: boolean,
  config: MovementConfig = DEFAULT_MOVEMENT,
): number {
  return (crouching ? config.crouchHeight : config.standHeight) - config.eyeDrop;
}

/** A fresh standing player at a spawn point. */
export function spawnState(pos: Vec3 = v3(0, 0, 0), yaw = 0): MovementState {
  return {
    pos: { x: pos.x, y: pos.y, z: pos.z },
    vel: v3(),
    yaw,
    pitch: 0,
    onGround: true,
    crouching: false,
  };
}
